import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
} from "@/hooks/useNotifications";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import { openNotificationTarget } from "@/lib/notificationNavigation";
import type { NotificationInboxItem } from "@/services/notifications";
import { useState } from "react";

import { NotificationItemRow } from "./NotificationItemRow";

const PANEL_LIMIT = 12;

export const NotificationBell = () => {
  const { user } = useAuth();
  const { isAdmin } = useUserRoles();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const countQuery = useUnreadNotificationCount();
  const listQuery = useNotifications({ limit: PANEL_LIMIT });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  if (!user) return null;

  const unread = countQuery.data ?? 0;
  const items = listQuery.data ?? [];
  const hasItems = items.length > 0;
  const isLoading = listQuery.isLoading;
  const isError = listQuery.isError;

  const badgeLabel = unread > 9 ? "9+" : String(unread);

  const handleOpen = (item: NotificationInboxItem) => {
    if (item.status === "unread") {
      markRead.mutate(item.inbox_id);
    }
    setOpen(false);
    // Centralized safe-navigation: ignores empty/invalid/external links and
    // falls back to type-aware defaults. Never throws.
    openNotificationTarget(item, navigate, { isAdmin });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("notifications.title")}
          className="relative h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground hover:bg-primary/[0.06] transition-colors duration-300"
        >
          <Bell className="h-4 w-4" strokeWidth={1.75} />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className={cn(
                "absolute -top-0.5 -right-0.5 min-w-[1.05rem] h-[1.05rem] px-1",
                "rounded-full bg-primary text-primary-foreground",
                "text-[10px] font-semibold leading-[1.05rem] text-center",
                "ring-2 ring-background",
              )}
            >
              {badgeLabel}
            </span>
          )}
          <span className="sr-only">
            {unread > 0 ? `${unread} ${t("notifications.unread")}` : t("notifications.title")}
          </span>
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0 z-[200] pt-safe pb-safe"
      >
        {/* Header — matches UserMenuSheet for visual consistency.
            Right padding leaves room for the built-in Sheet close (X). */}
        <div className="px-5 pt-5 pb-4 pr-14 border-b border-border/40 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-primary/[0.08] text-primary flex items-center justify-center flex-shrink-0">
            <Bell className="h-4 w-4" strokeWidth={1.75} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground truncate">
                {t("notifications.title")}
              </h2>
              {unread > 0 && (
                <span className="rounded-full bg-primary/15 text-primary text-[10px] font-semibold px-1.5 py-0.5 leading-none">
                  {badgeLabel}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => markAll.mutate(undefined)}
              disabled={unread === 0 || markAll.isPending}
              className={cn(
                "mt-0.5 text-xs font-medium text-primary hover:underline disabled:text-muted-foreground/60 disabled:no-underline disabled:cursor-default",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded transition-colors duration-200",
              )}
            >
              {t("notifications.markAllRead")}
            </button>
          </div>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-3">
            {isLoading && (
              <div className="space-y-2">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="flex gap-3 px-3 py-2.5">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isLoading && isError && (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t("notifications.loadError")}
              </p>
            )}

            {!isLoading && !isError && !hasItems && (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t("notifications.empty")}
              </p>
            )}

            {!isLoading && !isError && hasItems && (
              <div className="flex flex-col gap-0.5">
                {items.map((item) => (
                  <NotificationItemRow
                    key={item.inbox_id}
                    item={item}
                    onOpen={handleOpen}
                    compact
                  />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="border-t border-border/40 p-3">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
            className={cn(
              "w-full flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl",
              "text-sm font-medium text-foreground/90",
              "hover:bg-primary/[0.06] hover:text-foreground focus:bg-primary/[0.06]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "transition-colors duration-200",
            )}
          >
            {t("notifications.viewAll")}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default NotificationBell;